import React from "react";
import * as Icons from "lucide-react";
import MetricCard from "@/components/common/MetricCard";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { bandColor, publishRebuildHint } from "./seoUtils";

export default function SEOOverview({ data, loading, onOpenPage, onDiscover, onAdd, onIssues, onPages, onPublishCore, onPublishAllDrafts, onPublishBlog, canPublish, onRunCwv, onCheckAlerts, onRebuild }) {
  if (loading || !data) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-xl glass animate-pulse" />)}</div>;
  }
  const dist = data.distribution || {};
  const pages = data.pages_managed || 0;
  const analyzed = pages - (dist.not_analyzed || 0);
  const drafts = data.draft_count ?? (data.top_pages || []).filter((p) => (p.publish_state || p.status) !== "published").length;
  const measurement = data.measurement || {};
  const cwv = measurement.cwv || {};
  const alerts = measurement.alerts || [];
  const steps = [
    { n: 1, title: "Register URLs", done: pages > 0, hint: pages ? `${pages} pages in registry` : "Discover the site or add a URL", action: pages ? onPages : onDiscover, label: pages ? "View pages" : "Discover URLs" },
    { n: 2, title: "Analyze pages", done: analyzed > 0, hint: analyzed ? `${analyzed} analyzed` : "Run Analyze on each page (or bulk from Pages)", action: onPages, label: "Open Pages" },
    { n: 3, title: "Fix critical issues", done: (data.critical_issues || 0) === 0 && pages > 0, hint: `${data.critical_issues || 0} critical · ${data.warnings || 0} warnings`, action: onIssues, label: "Review issues" },
    { n: 4, title: "Publish to live", done: pages > 0 && drafts === 0 && analyzed > 0, hint: drafts ? `${drafts} draft(s) not live yet` : "All registered pages published", action: onPages, label: "Publish from Pages" },
  ];
  return (
    <div className="space-y-6">
      <GlassCard className="p-4 border border-amber-500/20 bg-amber-500/5">
        <p className="text-xs text-amber-100/90 leading-relaxed">{publishRebuildHint(measurement)}</p>
        <p className="text-[11px] text-white/40 mt-1">Robots.txt and sitemap.xml update from admin within ~60s without a site rebuild.</p>
      </GlassCard>

      {(alerts.length > 0 || cwv.ok || measurement.pagespeed_configured !== undefined) && (
        <GlassCard className="p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-white">Measurement</h3>
            <div className="flex flex-wrap gap-2">
              {onCheckAlerts && <Button size="sm" variant="ghost" className="text-white/70 h-8" onClick={onCheckAlerts}>Check alerts</Button>}
              {onRunCwv && <Button size="sm" variant="ghost" className="text-white/70 h-8" onClick={onRunCwv}>Run CWV</Button>}
              {onRebuild && <Button size="sm" variant="ghost" className="text-white/70 h-8" onClick={onRebuild}>Rebuild site</Button>}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-xs text-white/65">
            <div>
              <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Core Web Vitals</div>
              {cwv.ok ? (
                <p>LCP {cwv.lcp_ms ?? "—"}ms · CLS {cwv.cls ?? "—"} · INP {cwv.inp_ms ?? "—"}ms · {cwv.source}</p>
              ) : (
                <p>{measurement.pagespeed_configured ? "No CWV run yet" : "Set PAGESPEED_API_KEY on backend for CrUX/Lighthouse"}</p>
              )}
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Deploy hook</div>
              <p>{measurement.deploy_hook?.configured ? (measurement.deploy_hook?.enabled === false ? "Configured · disabled" : "Configured · on publish") : "Set CLOUDFLARE_PAGES_DEPLOY_HOOK_URL"}</p>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Open alerts</div>
              <p>{alerts.length ? `${alerts.length} measurement alert(s)` : "No traffic/index drop alerts"}</p>
            </div>
          </div>
          {alerts.slice(0, 4).map((a) => (
            <button key={a.id || a.message} type="button" className="block w-full text-left text-xs text-amber-200/90 hover:text-amber-100" onClick={onIssues}>
              {a.message}
            </button>
          ))}
        </GlassCard>
      )}

      <GlassCard className="p-5">
        <h3 className="text-sm font-medium text-white mb-1">What to do</h3>
        <p className="text-xs text-white/50 mb-4">SEO Management works in four steps. Draft saves stay private until you Publish.</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
          {steps.map((s) => (
            <div key={s.n} className={`rounded-xl border p-4 ${s.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-black/20"}`}>
              <div className="flex items-center gap-2 text-xs text-white/40 mb-2">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${s.done ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/70"}`}>{s.done ? "✓" : s.n}</span>
                Step {s.n}
              </div>
              <div className="text-sm text-white font-medium">{s.title}</div>
              <p className="text-xs text-white/50 mt-1 mb-3 min-h-[32px]">{s.hint}</p>
              <Button size="sm" variant="ghost" className="text-white/80 h-8 px-2" onClick={s.action}>{s.label}</Button>
            </div>
          ))}
        </div>
      </GlassCard>

      {canPublish && (
        <GlassCard className="p-4 flex flex-wrap gap-2 items-center">
          <span className="text-xs text-white/50 mr-2">Quick publish:</span>
          <Button size="sm" variant="ghost" className="text-white/80 h-8" onClick={onPublishCore}>Publish core pages</Button>
          <Button size="sm" variant="ghost" className="text-white/80 h-8" onClick={onPublishAllDrafts} disabled={!drafts}>Publish all drafts ({drafts})</Button>
          <Button size="sm" variant="ghost" className="text-white/80 h-8" onClick={onPublishBlog}>Publish blog</Button>
        </GlassCard>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <button type="button" className="text-left" onClick={onPages}><MetricCard label="SEO Health" value={`${data.health ?? 0} / 100`} icon="Gauge" accent="#8B5CF6" /></button>
        <button type="button" className="text-left" onClick={onPages}><MetricCard label="Pages Managed" value={data.pages_managed ?? 0} icon="FileSearch" accent="#3B82F6" /></button>
        <button type="button" className="text-left" onClick={onIssues}><MetricCard label="Critical Issues" value={data.critical_issues ?? 0} icon="AlertTriangle" accent="#EF4444" /></button>
        <button type="button" className="text-left" onClick={onIssues}><MetricCard label="Warnings" value={data.warnings ?? 0} icon="AlertCircle" accent="#F59E0B" /></button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard className="p-5 lg:col-span-1">
          <h3 className="text-sm font-medium text-white mb-3">Health distribution</h3>
          {Object.entries({ excellent: "Excellent", good: "Good", needs_attention: "Needs attention", critical: "Critical", not_analyzed: "Not analyzed" }).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between text-sm py-1.5 text-white/70">
              <span>{label}</span><span className="text-white">{dist[key] || 0}</span>
            </div>
          ))}
          <div className="flex flex-col gap-2 mt-4">
            <Button onClick={onDiscover} className="btn-gradient text-white border-0 h-9">Discover website URLs</Button>
            <Button variant="ghost" onClick={onAdd} className="text-white/70 h-9">Add a single URL</Button>
          </div>
        </GlassCard>
        <GlassCard className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white">Priority issues</h3>
            <button type="button" className="text-xs text-white/50 hover:text-white" onClick={onIssues}>Open issues</button>
          </div>
          {(data.priority_issues || []).length === 0 ? <p className="text-sm text-white/40">No open issues. Run a crawl after adding pages.</p> : (
            <ul className="space-y-2">
              {data.priority_issues.map((issue) => (
                <li key={issue.rule_id}>
                  <button type="button" onClick={onIssues} className="w-full flex items-start gap-2 text-sm text-white/70 hover:text-white text-left">
                    <Icons.AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                    <span className="flex-1">{issue.message}</span>
                    <span className="text-white/40">{issue.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </GlassCard>
      </div>

      <GlassCard className="p-0 overflow-hidden">
        <div className="px-5 py-3 text-sm font-medium text-white flex justify-between">
          <span>Top pages</span>
          <span className="text-xs font-normal text-white/40">Draft ≠ live until Publish</span>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-white/40">
            <tr><th className="px-5 py-2 text-left">Page</th><th className="px-5 py-2 text-left">URL</th><th className="px-5 py-2 text-left">Score</th><th className="px-5 py-2 text-left">Status</th></tr>
          </thead>
          <tbody>
            {(data.top_pages || []).map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => onOpenPage(p)}>
                <td className="px-5 py-3 text-white">{p.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-white/50">{p.path}</td>
                <td className="px-5 py-3" style={{ color: bandColor(p.seo_score) }}>{p.seo_score ?? "—"}</td>
                <td className="px-5 py-3 text-white/50">{p.publish_state || p.status}</td>
              </tr>
            ))}
            {(data.top_pages || []).length === 0 && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-white/40">
                No analyzed pages yet.{" "}
                <button type="button" className="text-cyan-300" onClick={onDiscover}>Discover URLs</button>
                {" "}or{" "}
                <button type="button" className="text-cyan-300" onClick={onAdd}>add one</button>.
              </td></tr>
            )}
          </tbody>
        </table>
      </GlassCard>
      <p className="text-xs text-white/40">Search Console and rankings are not connected yet — those integrations stay under Settings when ready.</p>
    </div>
  );
}
